/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);

const DATE_ACTE_FORMAT = 'DD/MM/YYYY';

/** Options de `validationInformationsNaissance`, tous les défauts préservent le comportement naissance existant. */
interface ValidationInformationsNaissanceOptions {
  /** Contrôle que le lieu de naissance est renseigné (défaut `true`). `false` en reconnaissance (pas de bloc lieu de naissance enfant, décision produit). */
  controleLieuNaissance?: boolean;
  /**
   * L'enfant n'est **pas encore né** (défaut `false`) : reconnaissance prénatale,
   * sous-type `N`. Rien de son état civil n'est alors exigé — ni nom, ni prénom,
   * ni sexe, ni date de naissance — et le contrôle « né avant l'acte » est levé,
   * puisqu'il naîtra précisément *après*.
   *
   * Un seul drapeau pour ces quatre conséquences, parce qu'elles découlent d'un
   * seul fait. Les données le confirment : sur 1079 reconnaissances prénatales de
   * la base de référence, 108 n'ont aucun nom d'enfant — c'est un cas normal, pas
   * une saisie incomplète.
   *
   * ⚠️ Sans effet sur un parent (`parent = true`) : un parent est toujours né.
   */
  enfantNonNe?: boolean;
}

/**
 * Valide l'état civil d'un individu (enfant ou parent) : nom, prénom, sexe,
 * date et lieu de naissance.
 *
 * Réutilisée pour l'enfant ET pour les parents (même logique que
 * `administres-app-recensement-citoyen`) :
 * - si `parent` et `filiationInconnue`, aucun contrôle (les champs sont grisés) ;
 * - si `parent` et `dateNaissanceEnfant` fournie, le parent doit être né avant l'enfant ;
 * - si enfant (`!parent`), la date de naissance doit précéder la date de l'acte
 *   (`data.dateActe`, présente dans le même objet), sauf si désactivé via `options`.
 *
 * Les clés correspondent aux `name` des champs (sans préfixe `parentN.`).
 *
 * @param options Réglages fins des contrôles enfant ; tous les défauts
 * reproduisent le comportement naissance existant, donc aucun appelant
 * existant n'est impacté par cet ajout.
 */
const validationInformationsNaissance = (
  data: Record<string, any>,
  parent: boolean = false,
  filiationInconnue: boolean = false,
  dateNaissanceEnfant?: {
    anneeNaissance: number;
    moisNaissance: number;
    jourNaissance: number;
  },
  options: ValidationInformationsNaissanceOptions = {},
) => {
  const { controleLieuNaissance = true, enfantNonNe = false } = options;

  /** Un parent est toujours né : l'assouplissement ne vise que l'enfant. */
  const pasEncoreNe = !parent && enfantNonNe;

  const errors = {
    dateNaissance: '',
    sexe: '',
    nomFamille: '',
    prenom: '',
    heureNaissance: '',
    minuteNaissance: '',
    adresseNaissance: '',
  };

  if (parent && filiationInconnue) {
    return {};
  }

  const { anneeNaissance, moisNaissance, jourNaissance } = data ?? {};

  const dateNaissance = dayjs(
    `${anneeNaissance}-${moisNaissance}-${jourNaissance}`,
    'YYYY-M-D',
  );

  const dateNaissanceRenseignee =
    !_.isNil(anneeNaissance) &&
    !_.isNil(moisNaissance) &&
    !_.isNil(jourNaissance);

  if (!dateNaissanceRenseignee) {
    if (!pasEncoreNe) {
      errors.dateNaissance = 'La date de naissance est obligatoire';
    }
  } else if (
    // Pas de borne basse : les registres numérisés remontent avant 1900
    // (AD-13181). Le refus des dates antérieures est propre au PACS.
    !dateNaissance.isValid() ||
    !dateNaissance.isSameOrBefore(dayjs(), 'day')
  ) {
    errors.dateNaissance = 'La date de naissance est invalide';
  }

  // L'enfant doit être né avant (ou le jour) du dressage de l'acte
  if (
    !parent &&
    !pasEncoreNe &&
    dateNaissance.isValid() &&
    !_.isEmpty(data?.dateActe)
  ) {
    const dateActe = dayjs(data.dateActe, DATE_ACTE_FORMAT, true);
    if (dateActe.isValid() && dateNaissance.isAfter(dateActe, 'day')) {
      errors.dateNaissance =
        "La date de naissance doit être antérieure à la date de l'acte";
    }
  }

  // Vérification que le parent est plus âgé que l'enfant
  if (parent && dateNaissance.isValid() && dateNaissanceEnfant) {
    const dateNaissanceRecense = dayjs(
      `${dateNaissanceEnfant.anneeNaissance}-${dateNaissanceEnfant.moisNaissance}-${dateNaissanceEnfant.jourNaissance}`,
      'YYYY-M-D',
    );
    if (
      dateNaissanceRecense.isValid() &&
      dateNaissance.isSameOrAfter(dateNaissanceRecense, 'day')
    ) {
      errors.dateNaissance = "Le parent doit être né avant l'enfant";
    }
  }

  // Tant que l'enfant n'est pas né, aucune de ces trois données n'est connue :
  // le sexe et le prénom peuvent ne pas encore être choisis, et le nom dépend
  // de la filiation qui s'établit à la naissance.
  if (!pasEncoreNe) {
    if (_.isEmpty(data?.sexe)) {
      errors.sexe = 'Le sexe est obligatoire';
    }

    if (_.isEmpty(data?.nomFamille)) {
      errors.nomFamille = 'Le nom de famille est obligatoire';
    }

    if (_.isEmpty(data?.prenom)) {
      errors.prenom = 'Le prénom usuel est obligatoire';
    }
  }

  // Si les heures sont renseignées mais pas les minutes (et inversement)
  if (!_.isNil(data?.heureNaissance) && _.isNil(data?.minuteNaissance)) {
    errors.minuteNaissance = 'Les minutes sont obligatoires';
  }

  if (!_.isNil(data?.minuteNaissance) && _.isNil(data?.heureNaissance)) {
    errors.heureNaissance = "L'heure de naissance est obligatoire";
  }

  if (
    controleLieuNaissance &&
    _.isEmpty(data?.adresseNaissance?.libelleCommune)
  ) {
    errors.adresseNaissance = 'Le lieu de naissance est obligatoire';
  }

  return _.pickBy(errors, (value) => value !== '');
};

export default validationInformationsNaissance;
